/**
 * RT Messaging WebSocket Client
 *
 * Type-safe client for the RT Messaging WebSocket API.
 * Uses generated types from AsyncAPI specification.
 */

import { Observable, Subject } from "rxjs";
import {
  BaseAsyncAPIClient,
  WebSocketClientConfig,
  WebSocketClientState,
} from "./base-asyncapi-client";
import { rtMessagingAnyMessageSchema } from "../../../../validators/rt-messaging-unions";
import {
  // Operations (client → server)
  Subscribe,
  Unsubscribe,
  MarkSeen,
  WatchEditors,
  WatchProjectData,
  WatchJobEvents,
  LockEditor,
  UnlockEditor,
  CurrentEditor,
  GetEntries,
  // Responses (server → client)
  Unsubscribed,
  Marked,
  Complete,
  ChannelList,
  SubscriptionList,
  Subscribed,
  NewEntries,
  DataChanged,
  JobEvent,
  CurrentEditorResponse,
  // Schema types
  SubscriptionInfo,
  EntryInfo,
  CurrentEditorInfo,
  GetEntriesParams,
  JobExecKind,
  ExecStatus,
} from "@app/shared/interfaces/rt-messaging.generated";

/**
 * Normalized job/step execution event (SCRUM-788).
 *
 * A camelCased projection of the server's `job-event` push. Job-level events
 * carry only the job fields; step-level events (`kind === 'step'`) additionally
 * carry `execStepId`, `stepId`, and `stepIndex`.
 */
export interface RTJobEvent {
  kind: JobExecKind;
  projectId: number;
  routineId: number;
  jobId: number;
  status: ExecStatus;
  execStepId: number | null;
  stepId: number | null;
  stepIndex: number | null;
}

/**
 * RT Messaging operations (client → server)
 * Note: These types include trans_id which will be added by the base client
 */
export type RTMessagingOperation =
  | Subscribe
  | Unsubscribe
  | GetEntries
  | MarkSeen
  | WatchEditors
  | WatchProjectData
  | WatchJobEvents
  | LockEditor
  | UnlockEditor
  | CurrentEditor
  | { message: "list-channels"; trans_id: number }
  | { message: "list-subscriptions"; trans_id: number };

/**
 * RT Messaging responses (server → client with trans_id)
 */
export type RTMessagingResponse =
  | ChannelList
  | SubscriptionList
  | Subscribed
  | Unsubscribed
  | Marked
  | Complete
  | CurrentEditorResponse;

/**
 * Current editor push notification (server → client, no trans_id or trans_id: 0)
 */
type CurrentEditorPushNotification = {
  message: "current-editor";
  editor: CurrentEditorInfo;
  trans_id?: number;
  [k: string]: unknown;
};

/**
 * RT Messaging push notifications (server → client, no trans_id or trans_id: 0)
 */
export type RTMessagingPushNotification =
  | NewEntries
  | DataChanged
  | JobEvent
  | CurrentEditorPushNotification;

/**
 * RT Messaging WebSocket client
 *
 * Provides type-safe access to all RT Messaging operations:
 * - Channel and subscription management
 * - Notification entry retrieval and marking
 * - Editor lock management
 * - Real-time push notifications
 */
export class RTMessagingClient extends BaseAsyncAPIClient<
  RTMessagingOperation,
  RTMessagingResponse,
  RTMessagingPushNotification
> {
  /** New notification entries from server */
  readonly newEntries$ = new Subject<EntryInfo[]>();

  /** Current editor changes */
  readonly editorChanges$ = new Subject<CurrentEditorInfo>();

  /** Project data changes */
  private readonly dataChanged$ = new Subject<{ projectId: number; changeType: string; contextId: number | null }>();

  /** Job/step execution events (SCRUM-788) */
  private readonly jobEvents$ = new Subject<RTJobEvent>();

  constructor(url: string, config?: Partial<WebSocketClientConfig>) {
    super({
      url,
      // Enable Zod validation by default (non-strict mode)
      responseSchema: rtMessagingAnyMessageSchema,
      strictValidation: false,
      ...config,
    });
  }

  // ==================== Channel & Subscription Management ====================

  /**
   * List all notification channels with their events
   */
  async listChannels(): Promise<ChannelList['channels']> {
    const response = await this.request<ChannelList>({
      message: "list-channels",
    });
    return response.channels || [];
  }

  /**
   * List current user's subscriptions
   */
  async listSubscriptions(): Promise<SubscriptionInfo[]> {
    const response = await this.request<SubscriptionList>({
      message: "list-subscriptions",
    });
    return response.subscriptions || [];
  }

  /**
   * Subscribe to a notification event
   *
   * @param eventId Event to subscribe to
   * @param projectId Optional project scope
   */
  async subscribe(
    eventId: number,
    projectId?: number
  ): Promise<SubscriptionInfo> {
    const response = await this.request<Subscribed>({
      message: "subscribe",
      event_id: eventId,
      project_id: projectId !== undefined ? projectId : undefined,
    } as Omit<Subscribe, "trans_id">);
    return response.subscription;
  }

  /**
   * Unsubscribe from a notification event
   *
   * @param subId Subscription ID to unsubscribe
   */
  async unsubscribe(subId: number): Promise<void> {
    await this.request<Unsubscribed>({
      message: "unsubscribe",
      sub_id: subId,
    } as Omit<Unsubscribe, "trans_id">);
  }

  // ==================== Notification Entry Management ====================

  /**
   * Get notification entries with optional filtering
   *
   * @param params Filter parameters
   */
  async getEntries(params?: GetEntriesParams): Promise<EntryInfo[]> {
    // Always include `since: null` — the server's custom deserializer
    // for this field requires it to be present in the JSON payload.
    const fullParams = { since: null, ...params };
    const response = await this.request<any>({
      message: "get-entries",
      params: fullParams,
    } as Omit<GetEntries, "trans_id">);
    return response.entries || [];
  }

  /**
   * Mark notification entries as seen
   *
   * @param entryIds Array of entry IDs to mark
   */
  async markSeen(entryIds: number[]): Promise<void> {
    await this.request<Marked>({
      message: "mark-seen",
      entry_ids: entryIds,
    } as Omit<MarkSeen, "trans_id">);
  }

  // ==================== Editor Lock Management ====================

  /**
   * Enable/disable watching editor changes
   *
   * @param watch true to enable, false to disable
   */
  async watchEditors(watch: boolean): Promise<void> {
    await this.request<Complete>({
      message: "watch-editors",
      watch,
    } as Omit<WatchEditors, "trans_id">);
  }

  /**
   * Enable/disable watching project data changes
   *
   * @param projectId Project to watch
   * @param watch true to enable, false to disable
   */
  async watchProjectData(projectId: number, watch: boolean): Promise<void> {
    await this.request<Complete>({
      message: "watch-project-data",
      project_id: projectId,
      watch,
    } as Omit<WatchProjectData, "trans_id">);
  }

  /**
   * Enable/disable watching job/step execution events for a project (SCRUM-788).
   *
   * Project-scoped, mirroring {@link watchProjectData}. Once watching, the server
   * pushes `job-event` messages for every job in the project; consumers filter
   * by `jobId` via the {@link jobEvents} observable.
   *
   * @param projectId Project to watch
   * @param watch true to enable, false to disable
   */
  async watchJobEvents(projectId: number, watch: boolean): Promise<void> {
    await this.request<Complete>({
      message: "watch-job-events",
      project_id: projectId,
      watch,
    } as Omit<WatchJobEvents, "trans_id">);
  }

  /**
   * Lock a project for editing
   *
   * @param projectId Project to lock
   */
  async lockEditor(projectId: number): Promise<void> {
    await this.request<Complete>({
      message: "lock-editor",
      project_id: projectId,
    } as Omit<LockEditor, "trans_id">);
  }

  /**
   * Unlock a project
   *
   * @param projectId Project to unlock
   */
  async unlockEditor(projectId: number): Promise<void> {
    await this.request<Complete>({
      message: "unlock-editor",
      project_id: projectId,
    } as Omit<UnlockEditor, "trans_id">);
  }

  /**
   * Get current editor status for a project
   *
   * @param projectId Project to check
   */
  async getCurrentEditor(projectId: number): Promise<CurrentEditorInfo> {
    const response = await this.request<CurrentEditorResponse>({
      message: "current-editor",
      project_id: projectId,
    } as Omit<CurrentEditor, "trans_id">);
    return response.editor;
  }

  // ==================== Observables for Push Notifications ====================

  /**
   * Observable of new notification entries
   */
  get newEntries(): Observable<EntryInfo[]> {
    return this.newEntries$.asObservable();
  }

  /**
   * Observable of editor lock status changes
   */
  get editorChanges(): Observable<CurrentEditorInfo> {
    return this.editorChanges$.asObservable();
  }

  /**
   * Observable of project data changes
   */
  get dataChanges(): Observable<{ projectId: number; changeType: string; contextId: number | null }> {
    return this.dataChanged$.asObservable();
  }

  /**
   * Observable of job/step execution events (SCRUM-788).
   * Emits for every job in a watched project; filter by `jobId`.
   */
  get jobEvents(): Observable<RTJobEvent> {
    return this.jobEvents$.asObservable();
  }

  // ==================== Lifecycle Hooks ====================

  protected onConnected(): void {
    this.log("RT Messaging client connected");
    // Subclass can override to perform initial setup
  }

  protected onPushNotification(
    message: RTMessagingPushNotification
  ): void {
    // Handle new entries
    if (message.message === "new-entries") {
      const newEntries = message as NewEntries;
      this.newEntries$.next(newEntries.entries || []);
    }
    
    // Handle project data changes
    if (message.message === "data-changed") {
      const dataChanged = message as DataChanged;
      this.dataChanged$.next({
        projectId: dataChanged.project_id,
        changeType: dataChanged.change_type,
        // SCRUM-751: workflow id for workflow-scoped change types; null otherwise.
        contextId: dataChanged.context_id ?? null,
      });
    }

    // Handle job/step execution events (SCRUM-788). Normalize to camelCase and
    // emit; consumers filter by jobId. Step-level fields are absent on job-level
    // events and coalesced to null.
    if (message.message === "job-event") {
      const jobEvent = message as JobEvent;
      this.jobEvents$.next({
        kind: jobEvent.kind,
        projectId: jobEvent.project_id,
        routineId: jobEvent.routine_id,
        jobId: jobEvent.job_id,
        status: jobEvent.status,
        execStepId: jobEvent.exec_step_id ?? null,
        stepId: jobEvent.step_id ?? null,
        stepIndex: jobEvent.step_index ?? null,
      });
    }

    // Handle current editor changes (push notifications from server)
    // These come as push notifications when someone else locks/unlocks a project
    if (message.message === "current-editor") {
      const editorMessage = message as CurrentEditorPushNotification;
      const editor = editorMessage['editor'];
      if (editor && typeof editor === 'object' && 'projectId' in editor) {
        this.editorChanges$.next(editor as CurrentEditorInfo);
      }
    }
  }

  // ==================== Helper Methods ====================

  /**
   * Check if client is connected
   */
  get isConnected(): boolean {
    return this.state$.value === WebSocketClientState.CONNECTED;
  }

  /**
   * Wait for connection to be established
   */
  async waitForConnection(): Promise<void> {
    if (this.isConnected) {
      return;
    }

    return new Promise((resolve, reject) => {
      const subscription = this.state$.subscribe((state) => {
        if (state === WebSocketClientState.CONNECTED) {
          subscription.unsubscribe();
          resolve();
        } else if (state === WebSocketClientState.FAILED) {
          subscription.unsubscribe();
          reject(new Error("Connection failed"));
        }
      });
    });
  }
}
